<template>
	<div class="nav">
		<v-navigation-drawer
			v-if="!isShipperOnlyUser"
			v-model="showSideNav"
			app
			:absolute="mobileNavMode"
			:temporary="mobileNavMode"
			:permanent="mobileNavMode == false"
			class="nav-drawer"
		>
			<div class="nav-drawer-header">
				<v-btn depressed text to="/home">
					<i class="icon-tl" style="color: white" />
				</v-btn>

				<v-btn
					v-if="mobileNavMode"
					class="nav-drawer-header-close"
					icon
					@click="showSideNav = !showSideNav"
				>
					<v-icon>mdi-chevron-left</v-icon>
				</v-btn>
			</div>
			<div class="nav-drawer-list">
				<v-list>
					<template v-for="link in accessibleSideNavLinks">
						<v-list-group
							v-if="link.subLinks"
							:key="link.title"
							v-model="link.active"
							color="white"
						>
							<template v-slot:activator>
								<v-list-item-content>
									<v-list-item-title v-text="link.title"></v-list-item-title>
								</v-list-item-content>
							</template>

							<v-list-item
								v-for="subLink in link.subLinks.filter(canAccessLink)"
								:key="subLink.name"
								:to="subLink.to"
								:class="{ 'v-list-item--active': isLinkActive(subLink.to) }"
								color="white"
								link
							>
								<v-list-item-content>
									<v-list-item-title v-text="subLink.name"></v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-list-group>

						<v-list-item v-else :key="link.title" :to="link.to" color="white" link>
							<v-list-item-content>
								<v-list-item-title v-text="link.title" class="nav-link"></v-list-item-title>
							</v-list-item-content>
						</v-list-item>
					</template>
				</v-list>
			</div>
		</v-navigation-drawer>

		<v-app-bar app class="nav-bar" color="white">
			<v-btn v-if="mobileNavMode && !isShipperOnlyUser" icon @click="showSideNav = !showSideNav">
				<v-icon>mdi-menu</v-icon>
			</v-btn>
			<v-toolbar-title class="nav-bar-title">{{ $route.name }}</v-toolbar-title>

			<v-spacer></v-spacer>

			<!-- ezeep Printer Health Indicator -->
			<v-tooltip bottom>
				<template v-slot:activator="{ on, attrs }">
					<v-btn
						icon
						small
						v-bind="attrs"
						v-on="on"
						@click="canAccessPrinterSettings ? $router.push('/settings/printers').catch(() => {}) : null"
						:style="canAccessPrinterSettings ? '' : 'cursor: default'"
						class="ezeep-health-indicator mr-1"
					>
						<v-badge
							:color="ezeepStatusColor"
							dot
							overlap
							bordered
						>
							<v-icon size="20" :color="ezeepStatusColor === 'grey' ? 'grey' : undefined">mdi-printer</v-icon>
						</v-badge>
					</v-btn>
				</template>
				<span>{{ ezeepStatusMessage }}</span>
			</v-tooltip>

			<v-menu bottom max-width="15.2rem" rounded offset-y class="nav-account">
				<template v-slot:activator="{ on }">
					<v-btn
						:class="['nav-account-icon', { 'nav-highlight': highlightUserNav }]"
						icon
						v-on="on"
					>
						<v-avatar class="avatar" color="grey darken-1" size="38">
							<span class="white--text text-h5">
								{{ $store.getters.userInfo?.initials }}
							</span>
						</v-avatar>
					</v-btn>
				</template>
				<v-card>
					<v-list-item-content class="nav-account-list">
						<template v-for="link in userLinks">
							<div :key="link.name">
							<v-btn
								v-if="!link.isToggle"
								:to="link.to"
								class="justify-start"
								depressed
								text
							>
								{{ link.name }}
							</v-btn>
							<v-btn
								v-else
								@click="handleToggleAction(link.action)"
								class="justify-start"
								depressed
								text
							>
								{{ link.name }}
								<v-spacer></v-spacer>
								<v-icon x-small :color="getToggleIconColor(link.action)">
									{{ getToggleIcon(link.action) }}
								</v-icon>
							</v-btn>
							</div>
						</template>
						<v-divider></v-divider>
						<v-btn depressed text @click="logout"> Log Out </v-btn>
					</v-list-item-content>
				</v-card>
			</v-menu>
		</v-app-bar>
	</div>
</template>

<script>
import { SystemPermissions, hasAnyPermission, hasPermission, WellKnownRoles, hasRole, hasAnyRole, shouldEnableHardware } from "@/models/User";

export default {
	name: "Navigation",
	data() {
		return {
			windowSize: window.innerWidth,
			mobileNavMode: false,
			showSideNav: null,
			hardwareWidgetVisible: true,
			highlightUserNav: false,
			sideNavLinks: [
				{
					active: false,
					title: "Sales Orders",
					subLinks: [
						{
							name: "Quotes",
							to: "/sales-orders/quotes",
							permissionsRequired: [SystemPermissions.SalesOrders_Quote_View],
						},
						{
							name: "Pending Approvals",
							to: "/sales-orders/pending-approvals",
							permissionsRequired: [SystemPermissions.SalesOrders_PendingApproval_View],
						},
						{
							name: "Sample Orders",
							to: "/sales-orders/sample-orders",
							permissionsRequired: [SystemPermissions.SalesOrders_SampleOrder_View],
						},
						{
							name: "Orders",
							to: "/sales-orders/orders",
							permissionsRequired: [SystemPermissions.SalesOrders_Order_View],
						},
						{
							name: "Find Related Orders",
							to: "/sales-orders/find-related",
							permissionsRequired: [SystemPermissions.SalesOrders_FindRelatedOrders_Search],
						},
					],
				},
				{
					active: false,
					title: "Purchasing",
					subLinks: [
						{
							name: "Purchase Orders",
							to: "/purchasing/purchase-orders",
							permissionsRequired: [SystemPermissions.Purchasing_PurchaseOrders_View],
						},
						{
							name: "POs to Generate",
							to: "/purchasing/po-generate",
							permissionsRequired: [
								SystemPermissions.Purchasing_POsToGenerate_Search,
								SystemPermissions.Purchasing_POsToGenerate_PlaceStockPO,
							],
						},
						{
							name: "Supplier",
							to: "/purchasing/supplier",
							permissionsRequired: [SystemPermissions.Purchasing_Supplier_Search_View],
						},
					],
				},
				{
					active: false,
					title: "Receiving",
					subLinks: [
						{
							name: "Receiving Entry",
							to: "/receiving/receiving-entry",
							permissionsRequired: [SystemPermissions.Receiving_ReceivingEntry_View],
						},
						{
							name: "Commits",
							to: "/receiving/commits",
							permissionsRequired: [SystemPermissions.Receiving_Commits_Search_View],
						},
					],
				},				
				{
					active: false,
					title: "Inventory",
					subLinks: [
						{
							name: "Available Inventory",
							to: "/inventory/available-inventory",
							permissionsRequired: [SystemPermissions.Inventory_AvailableInventory_Search_View],
						},
						{
							name: "Inventory Items",
							to: "/inventory/items",
							permissionsRequired: [SystemPermissions.Inventory_InventoryItems_View_Search],
						},
						{
							name: "Bin Utility",
							to: "/inventory/bin-utility",
							permissionsRequired: [SystemPermissions.Inventory_BinUtility_View_Search],
						},
						{
							name: "Size Charts",
							to: "/inventory/size-charts",
							permissionsRequired: [SystemPermissions.Inventory_SizeChart_View_Search],
						},
						{
							name: "Color Palette",
							to: "/inventory/color-palette",
							permissionsRequired: [SystemPermissions.Inventory_ColorPalette_View_Search],
						},
						{
							name: "Package Category",
							to: "/inventory/package-category",
							permissionsRequired: [SystemPermissions.Inventory_PackageCategory_View_Search],
						},
						{
							name: "Sub-Item Category",
							to: "/inventory/sub-item-category",
							permissionsRequired: [SystemPermissions.Inventory_SubItemCategory_View_Search],
						},
						{
							name: "Position Of Letters",
							to: "/inventory/position-of-letters",
							permissionsRequired: [SystemPermissions.Inventory_PositionOfLetters_View],
						},
						{
							name: "Process Types",
							to: "/inventory/processes",
							permissionsRequired: [SystemPermissions.Inventory_ProcessTypes_View],
						},
						// {
						// 	name: "Demand History",
						// 	to: "/inventory/demand-history",
						// 	permissionsRequired: [SystemPermissions.Reports_DemandHistory_ALL],
						// },
					],
				},
				{
					active: false,
					title: "Relationships",
					subLinks: [
						{
							name: "Customer",
							to: "/relationships/customer",
							permissionsRequired: [SystemPermissions.Relationships_Customer_ALL],
						},
						{
							name: "Vendor",
							to: "/relationships/vendor",
							permissionsRequired: [SystemPermissions.Relationships_Vendor_ALL],
						},
						// {
						// 	name: "Rep",
						// 	to: "/relationships/rep",
						// 	// permissionsRequired: [SystemPermissions.Relationships_Rep_ALL],
						// },
					],
				},				
				{
					active: false,
					title: "Reports",
					to: "/reports",
				},
				{
					active: false,
					title: "Warehouse",
					subLinks: [
						{
							name: "Shipping Dashboard",
							to: "/shipper",
							permissionsRequired: [SystemPermissions.Shipper_Shipping_ViewPickTicket],
						},
						{
							name: "Recent Activity",
							to: "/shipper/recent-activity",
							permissionsRequired: [SystemPermissions.Shipper_RecentActivity_View],
						},
						{
							name: "End of Day",
							to: "/shipper/end-of-day",
							permissionsRequired: [SystemPermissions.Shipper_EndOfDay_View],
						},
					],
				},
				{
					active: false,
					title: "Settings",
					subLinks: [
						{
							name: "Customer Category",
							to: "/settings/customer-category",
							permissionsRequired: [SystemPermissions.Settings_CustomerCategory_ALL],
						},
						{
							name: "Payment Method",
							to: "/settings/payment-method",
							permissionsRequired: [SystemPermissions.Settings_PaymentMethod_ALL],
						},
						{
							name: "Payment Terms",
							to: "/settings/payment-terms",
							permissionsRequired: [SystemPermissions.Settings_PaymentTerms_ALL],
						},
						{
							name: "Shipping Method",
							to: "/settings/shipping-method",
							permissionsRequired: [SystemPermissions.Settings_ShippingMethod_ALL],
						},
						{
							name: "Messages",
							to: "/settings/messages",
							permissionsRequired: [SystemPermissions.Settings_Messages_ALL],
						},
						{
							name: "Security",
							to: "/settings/security",
							permissionsRequired: [SystemPermissions.Settings_Security_ALL],
						},
						{
							name: "Printing Status",
							to: "/settings/printer-status",
							permissionsRequired: [SystemPermissions.Settings_PrintingLog_ALL],
						},
						{
							name: "Printer",
							to: "/settings/printers",
							permissionsRequired: [SystemPermissions.Settings_Printers_ALL],
						},
						{
							name: "Package Types",
							to: "/settings/package-types",
							permissionsRequired: [SystemPermissions.Settings_PackageTypes_ALL],
						},
						{
							name: "Zip Tax Rate",
							to: "/settings/zip-tax",
							permissionsRequired: [SystemPermissions.Settings_ZipTaxRate_ALL],
						},
						{
							name: "System Category",
							to: "/settings/system-category",
							permissionsRequired: [SystemPermissions.Settings_SystemCategory_ALL],
						},
					],
				},
			],
		};
	},
	watch: {
		windowSize(value) {
			if (value < 1500) {
				this.mobileNavMode = true;
			} else {
				this.mobileNavMode = false;
				this.showSideNav = true;
			}
		},
		$route() {
			this.setActiveState();
		},
	},
	created() {
		if (this.windowSize < 1500) {
			this.mobileNavMode = true;
			this.showSideNav = false;
		}

		window.addEventListener("resize", () => {
			this.windowSize = window.innerWidth;
		});

		this.setActiveState();

		// Initialize hardware widget visibility from localStorage
		const savedVisibility = localStorage.getItem('hardware_widget_visible');
		this.hardwareWidgetVisible = savedVisibility !== 'false';

		// Listen for hardware widget state changes from the widget itself
		this.$root.$on('hardware-widget-toggle', (visible) => {
			this.hardwareWidgetVisible = visible;
		});

		// Listen for user navigation highlight request
		this.$root.$on('highlight-user-navigation', (duration) => {
			this.triggerUserNavHighlight(duration);
		});

		// Start ezeep health polling
		this.$store.dispatch('ezeepHealth/startPolling');
	},
	beforeDestroy() {
		// Clean up event listeners
		this.$root.$off('hardware-widget-toggle');
		this.$root.$off('highlight-user-navigation');

		// Stop ezeep health polling
		this.$store.dispatch('ezeepHealth/stopPolling');
	},
	computed: {
		userLinks() {
			const links = [
				{
					name: "Account Settings",
					to: "/settings/account",
				},
			];

			// Only show Hardware Widget toggle for users with hardware access
			const user = this.$store.getters.userInfo;
			if (user && shouldEnableHardware(user)) {
				links.push({
					name: "Hardware Widget",
					action: "toggle-hardware-widget",
					isToggle: true,
				});
			}

			return links;
		},
		isShipperOnlyUser() {
			const user = this.$store.getters.userInfo;
			// User has ONLY the Shipper role (no Admin or Super Admin privileges)
			return user && hasRole(user, WellKnownRoles.SHIPPER) && !hasAnyRole(user, [WellKnownRoles.ADMIN, WellKnownRoles.SUPER_ADMIN]);
		},
		accessibleSideNavLinks() {
			// Shipper-only users don't need side navigation - they go directly to /shipper dashboard
			if (this.isShipperOnlyUser) {
				return [];
			}
			// Regular navigation for other users
			return this.sideNavLinks.filter(
				(link) => !link.subLinks || this.canAccessSublinks(link.subLinks)
			);
		},
		ezeepStatusColor() {
			return this.$store.getters['ezeepHealth/ezeepStatusColor'];
		},
		ezeepStatusMessage() {
			return this.$store.getters['ezeepHealth/ezeepStatusMessage'];
		},
		canAccessPrinterSettings() {
			const user = this.$store.getters.userInfo;
			if (!user) return false;
			return hasAnyRole(user, [WellKnownRoles.ADMIN, WellKnownRoles.SUPER_ADMIN]) ||
				hasPermission(user, SystemPermissions.Settings_Printers_ALL);
		},
	},
	methods: {
		canAccessLink(link) {
			const user = this.$store.getters.userInfo;
			// If no permissions are required for the link, allow access
			if (!link.permissionsRequired) return true;

			// If admin, allow full access
			if (hasAnyRole(user, [WellKnownRoles.ADMIN, WellKnownRoles.SUPER_ADMIN])) {
				return true;
			}

			// Check if the user has any of the required permissions for the link
			return hasAnyPermission(user, link.permissionsRequired);
		},
		canAccessSublinks(subLinks) {
			if (!Array.isArray(subLinks)) {
				return false;
			}

			const user = this.$store.getters.userInfo;
			return subLinks.some((subLink) => {
				// If no permissions are required for the sublink, allow access
				if (!subLink.permissionsRequired) return true;

				// If admin, allow full access
				if (hasAnyRole(user, [WellKnownRoles.ADMIN, WellKnownRoles.SUPER_ADMIN])) {
					return true;
				}

				// Check if the user has any of the required permissions for the sublink
				return hasAnyPermission(user, subLink.permissionsRequired);
			});
		},
		setActiveState() {
			// Get current route path and remove trailing slash if present
			let currentRoutePath = this.$route.path.replace(/\/$/, "");

			// Split path to handle nested routes
			const pathParts = currentRoutePath.split("/");

			// For routes like /sales-orders/orders/123/payments
			// we want to match against /sales-orders/orders
			const baseRoutePath =
				pathParts.length > 2
					? `/${pathParts[1]}/${pathParts[2]}` // Get base path like /sales-orders/orders
					: currentRoutePath;

			this.sideNavLinks.forEach((group) => {
				// Reset the active state before checking
				group.active = false;

				if (group.to === baseRoutePath) {
					// Direct match for non-nested links
					group.active = true;
				} else if (group.subLinks) {
					// Check sublinks
					group.subLinks.forEach((subLink) => {
						// If any sublink's base path matches our current base path
						if (subLink.to === baseRoutePath) {
							group.active = true;
						}
					});
				}
			});
		},
		isLinkActive(linkPath) {
			// Get current route path and split into parts
			const currentPath = this.$route.path.replace(/\/$/, "");
			const pathParts = currentPath.split("/");

			// For routes like /sales-orders/orders/123/payments
			// we want to match against /sales-orders/orders
			const baseRoutePath =
				pathParts.length > 2
					? `/${pathParts[1]}/${pathParts[2]}` // Get base path
					: currentPath;

			return linkPath === baseRoutePath;
		},
		logout() {
			this.$store
				.dispatch("logout")
				.then(() => {
					this.$router.push("/login");
				})
				.catch((error) => {
					console.log(error);
				});
		},
		handleToggleAction(action) {
			if (action === 'toggle-hardware-widget') {
				this.toggleHardwareWidget();
			}
		},
		toggleHardwareWidget() {
			this.hardwareWidgetVisible = !this.hardwareWidgetVisible;
			localStorage.setItem('hardware_widget_visible', this.hardwareWidgetVisible.toString());

			// Emit event to notify the hardware widget component
			this.$root.$emit('hardware-widget-toggle', this.hardwareWidgetVisible);

			// No toast needed - user can see the visual change
		},
		getToggleIcon(action) {
			if (action === 'toggle-hardware-widget') {
				return this.hardwareWidgetVisible ? 'mdi-eye' : 'mdi-eye-off';
			}
			return 'mdi-toggle-switch';
		},
		getToggleIconColor(action) {
			if (action === 'toggle-hardware-widget') {
				return this.hardwareWidgetVisible ? 'green' : 'grey';
			}
			return 'grey';
		},
		triggerUserNavHighlight(duration = 8000) {
			this.highlightUserNav = true;
			// Remove highlight after the specified duration (matches toast timeout)
			setTimeout(() => {
				this.highlightUserNav = false;
			}, duration);
		},
	},
};
</script>

<style lang="scss" scoped>
.nav {
	&-bar {
		box-shadow: rgba(50, 50, 93, 0.05) 0px 2px 5px -1px, rgba(0, 0, 0, 0.2) 0px 1px 3px -1px !important;
		&-icon {
			.v-icon {
				font-size: 3rem !important;
			}
		}
		&-title {
			font-size: 1.8rem;
		}
	}
	&-drawer {
		position: fixed;
		::v-deep .v-navigation-drawer__content {
			display: flex;
			flex-flow: column nowrap;
		}
		&-header {
			display: flex;
			align-items: center;
			flex-flow: row wrap;
			height: 64px; //match height with app bar
			padding: 1rem;
			background-color: $color-primary;
			box-shadow: rgba(50, 50, 93, 0.05) 0px 2px 5px -1px, rgba(0, 0, 0, 0.2) 0px 1px 3px -1px;

			.v-btn:not(.nav-drawer-header-close) {
				background-color: rgba($color-white, 0.4);
				min-width: initial !important;
				width: 4.5rem;
				height: 4rem !important;
				border-radius: 0.8rem;
				i {
					font-size: 2.5rem;
				}

				&.v-btn--active {
					background-color: rgba($color-gray-dark, 0.2);
				}
			}

			&-close {
				margin-left: auto;
				i.v-icon {
					font-size: 2.5rem;
					color: $color-white;
				}
			}
		}
		&-list {
			flex-grow: 1;
			color: $color-white;
			background-color: $color-gray-dark;
			padding: 1rem 0;
			.v-list-group::v-deep {
				.v-list-group__header {
					&.v-list-item--active {
						.v-list-item__title {
							font-weight: 700;
						}
					}
					.v-list-item__title {
						font-size: 1.6rem;
						font-weight: 300;
						text-transform: uppercase;
						color: $color-white;
						transition: all 0.2s ease;
					}
					.v-list-group__header__prepend-icon {
						margin-right: 16px;
						i {
							color: $color-white;
						}
					}
					.v-list-group__header__append-icon {
						margin-left: 0;
						min-width: initial;
						i {
							color: $color-white;
						}
					}
				}
				.v-list-group__items {
					.v-list-item {
						padding-left: 4rem;
						.v-list-item__title {
							color: $color-white;
							font-size: 1.4rem;
							font-weight: 300;
						}
					}
				}
			}
		}
	}
	&-account {
		&-icon {
			margin-right: 0 !important;
			margin-left: auto;
		}
		&-list {
			padding: 0;
			a {
				font-size: 1.1rem;
			}
			.v-btn {
				font-size: 1.1rem;
				margin: 0.25rem 0;
				justify-content: flex-start;
				&:last-child {
					margin-top: 0;
				}
			}
		}
	}
	&-link {
		font-size: 1.6rem;
		font-weight: 300;
		text-transform: uppercase;
		color: $color-white;
		transition: all 0.2s ease;
	}
}
.v-menu__content {
	top: 65px !important;
	box-shadow: rgba(50, 50, 93, 0.15) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px !important;
}

/* User navigation highlight animation */
.nav-highlight {
	position: relative;
	z-index: 1000;

	&::before {
		content: '';
		position: absolute;
		top: -6px;
		left: -6px;
		right: -6px;
		bottom: -6px;
		border-radius: 50%;
		background: linear-gradient(45deg, rgba(76, 175, 80, 0.8), rgba(139, 195, 74, 0.8));
		animation: highlight-glow 2s ease-in-out infinite;
		z-index: -1;
	}

	&::after {
		content: '';
		position: absolute;
		top: -8px;
		left: -8px;
		right: -8px;
		bottom: -8px;
		border-radius: 50%;
		border: 2px solid rgba(76, 175, 80, 0.6);
		animation: highlight-ring 2s ease-in-out infinite;
		z-index: -2;
	}

	.avatar {
		position: relative;
		z-index: 1;
		border: 2px solid rgba(255, 255, 255, 0.9) !important;
	}
}

@keyframes highlight-glow {
	0%, 100% {
		opacity: 0.6;
		transform: scale(1);
	}
	50% {
		opacity: 0.9;
		transform: scale(1.05);
	}
}

@keyframes highlight-ring {
	0%, 100% {
		opacity: 0.4;
		transform: scale(1);
	}
	50% {
		opacity: 0.8;
		transform: scale(1.1);
	}
}
</style>
